import React, { useState, useEffect } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import ImageZoomPan from "../ImageMagnifier/ImageZoomPan";
import "./JudgeDashboard.css";

const JudgeDashboard = () => {
  const [groupedSubmissions, setGroupedSubmissions] = useState([]);
  const [selectedUserGroup, setSelectedUserGroup] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [user, setUser] = useState(null);

  const getShortIdNumber = (userId) => {
    if (!userId) return "000";
    let sum = 0;
    for (let i = 0; i < userId.length; i++) sum += userId.charCodeAt(i);
    return ((sum % 999) + 1).toString().padStart(3, "0");
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return (window.location.href = "/login");

    const decoded = jwtDecode(token);
    if (decoded.role !== "judge") return (window.location.href = "/profile");

    const fetchData = async () => {
      setLoading(true);
      try {
        const [uRes, subRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/api/user/profile`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${process.env.REACT_APP_API_URL}/api/user/judge-dashboard`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        setUser(uRes.data);

        const grouped = subRes.data.reduce((acc, sub) => {
          const uid = sub.user._id || sub.user;
          if (!acc[uid]) acc[uid] = [];
          const imageFiles = sub.files
            .filter(f => {
              const fileUrl = typeof f === "string" ? f : f.url || "";
              return fileUrl && !/\.pdf($|\?)/i.test(fileUrl);
            })
            .slice(0, 2);
          if (imageFiles.length > 0) acc[uid].push({ ...sub, files: imageFiles });
          return acc;
        }, {});

        const groupedArray = Object.entries(grouped).map(([userId, subs]) => ({
          userId,
          submissions: subs.sort(
            (a, b) => new Date(b.submissionDate) - new Date(a.submissionDate)
          ),
        }));

        setGroupedSubmissions(groupedArray);
        if (groupedArray.length > 0) setSelectedUserGroup(groupedArray[0]);
      } catch (err) {
        console.error(err);
        setError("Failed to load data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSignOut = () => {
    localStorage.removeItem("token");
    window.location.href = "/login";
  };

  const filteredGroups = groupedSubmissions.filter(group => {
    const q = searchQuery.toLowerCase();
    return (
      group.userId.toLowerCase().includes(q) ||
      group.submissions.some(s => s._id.toLowerCase().includes(q))
    );
  });

  return (
    <div className="judge-dashboard">
      <div className="signout-container">
        <button className="signout-button" onClick={handleSignOut}>
          Sign Out
        </button>
      </div>

      <div className="welcome-message">
        {user ? `Welcome, ${user.firstName}!` : "Welcome!"}
      </div>

      <header className="dashboard-header">
        <h1>JUDGE PANEL</h1>
        <p className="subheading">Selected Entries for Review</p>
      </header>

      <div className="search-bar">
        <input
          placeholder="Search by User ID or Entry ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {error && <div className="error-message">{error}</div>}
      {loading ? (
        <div className="loading">Loading submissions...</div>
      ) : (
        <div className="dashboard-content">
          <div className="judge-submission-list">
            {filteredGroups.map(group => (
              <div key={group.userId} className="judge-user-group">
                <div
                  className={`judge-user-id ${
                    selectedUserGroup?.userId === group.userId ? "active" : ""
                  }`}
                  onClick={() => setSelectedUserGroup(group)}
                >
                  User #{getShortIdNumber(group.userId)}
                </div>
                {group.submissions.map(s => (
                  <div
                    key={s._id}
                    className={`judge-submission-list-item sub-item ${
                      selectedUserGroup?.submissions.some(sub => sub._id === s._id)
                        ? "active"
                        : ""
                    }`}
                    onClick={() => setSelectedUserGroup(group)}
                  >
                    Entry #{s._id.slice(-4)}
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="judge-submission-details">
            {selectedUserGroup ? (
              <>
                <div className="judge-submission-overview">
                  <h2>
                    Submissions for User #{getShortIdNumber(selectedUserGroup.userId)}
                  </h2>
                  <p>
                    <strong>Total Entries:</strong> {selectedUserGroup.submissions.length}
                  </p>
                </div>

                <div className="judge-submission-files-grid">
                  {selectedUserGroup.submissions.map(sub =>
                    sub.files.map((file, idx) => {
                      const fileUrl = typeof file === "string" ? file : file.url || "";
                      if (!fileUrl || /\.pdf($|\?)/i.test(fileUrl)) return null;

                      return (
                        <div
                          key={`${sub._id}-${idx}`}
                          className="judge-submission-image-wrapper"
                          onClick={() => setSelectedImage(fileUrl)}
                        >
                          <img
                            src={fileUrl}
                            alt={`Submission ${sub._id.slice(-4)} file ${idx + 1}`}
                          />
                          <div className="image-label">#{sub._id.slice(-4)}</div>
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            ) : (
              <p className="no-submissions">Select a user to view their submissions.</p>
            )}
          </div>
        </div>
      )}

      {selectedImage && (
        <div
          className="modal-overlay"
          onClick={() => setSelectedImage(null)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <ImageZoomPan src={selectedImage} maxScale={3} />
            <button
              className="modal-close"
              onClick={() => setSelectedImage(null)}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default JudgeDashboard;
